import moment from 'moment';

import { computePercentage } from '../../helpers/functions';

import { IAsset } from '../../types/model';
import { AssetInfo, MarketColumn } from './types';

const currency = 'usd';

export const sortAssets = (
  a: IAsset,
  b: IAsset,
  type: MarketColumn,
  getSortValue: (
    a: string | number | undefined | null,
    b: string | number | undefined | null
  ) => number
) => {
  const objA: AssetInfo = JSON.parse(a?.info?.data ?? '{}');
  const objB: AssetInfo = JSON.parse(b?.info?.data ?? '{}');

  // default type === 'ASSET'
  let valueA: string | number | undefined | null = !type ? null : a.name ?? '';
  let valueB: string | number | undefined | null = !type ? null : b.name ?? '';

  if (type === 'PRICE') {
    valueA = a.history?.[0]?.[currency] ?? 0;
    valueB = b.history?.[0]?.[currency] ?? 0;
  }

  if (type === 'VAR 24H') {
    valueA =
      objA?.marketData?.priceChangePercentage24hInCurrency?.[currency] ??
      computePercentage(
        a.history[1]?.[currency] ?? 0,
        a.history[0]?.[currency] ?? 0,
        100
      );
    valueB =
      objB?.marketData?.priceChangePercentage24hInCurrency?.[currency] ??
      computePercentage(
        b.history[1]?.[currency] ?? 0,
        b.history[0]?.[currency] ?? 0,
        100
      );
  }

  if (type === 'VAR 7D') {
    valueA =
      objA?.marketData?.priceChangePercentage7dInCurrency?.[currency] ??
      computePercentage(
        a.history[6]?.[currency] ?? 0,
        a.history[0]?.[currency] ?? 0,
        100
      );
    valueB =
      objB?.marketData?.priceChangePercentage7dInCurrency?.[currency] ??
      computePercentage(
        b.history[6]?.[currency] ?? 0,
        b.history[0]?.[currency] ?? 0,
        100
      );
  }

  if (type === 'MARKET CAP') {
    valueA = a?.marketCap ?? 0;
    valueB = b?.marketCap ?? 0;
  }

  if (type === 'ECOSYSTEM') {
    const ecosistemsA = Object.values(objA.categories ?? {});
    const ecosistemsB = Object.values(objB.categories ?? {});

    valueA =
      ecosistemsA.length === ecosistemsB.length
        ? ecosistemsA?.[0] ?? 0
        : ecosistemsA.length;
    valueB =
      ecosistemsA.length === ecosistemsB.length
        ? ecosistemsB?.[0] ?? 0
        : ecosistemsB.length;
  }

  if (type === 'INDUSTRY') {
    valueA = objA.industry;
    valueB = objB.industry;
  }

  if (type === 'COUPON') {
    valueA = objA.coupon ? +objA.coupon : 0;
    valueB = objB.coupon ? +objB.coupon : 0;
  }

  if (type === 'MATURITY DATE') {
    valueA = objA.maturityDate;
    valueB = objB.maturityDate;
  }

  if (type === 'RATING') {
    // https://www.fidelity.com/learning-center/investment-products/fixed-income-bonds/bond-ratings
    const rates = [
      'D',
      'C',
      'CC',
      'CCC-',
      'CCC',
      'CCC+',
      'B-',
      'B',
      'B+',
      'BB-',
      'BB',
      'BB+',
      'BBB-',
      'BBB',
      'BBB+',
      'A-',
      'A',
      'A+',
      'AA-',
      'AA',
      'AA+',
      'AAA',
    ];
    valueA = rates.findIndex((el) => el === objA.rating?.spRating);
    valueB = rates.findIndex((el) => el === objB.rating?.spRating);
  }

  if (type === 'COUNTRY') {
    valueA = a.country;
    valueB = b.country;
  }

  if (type === 'AVG30D') {
    valueA =
      a.history.slice(0, 29).reduce((x, y) => x + y[currency], 0) /
      a.history.slice(0, 29).length;
    valueB =
      b.history.slice(0, 29).reduce((x, y) => x + y[currency], 0) /
      b.history.slice(0, 29).length;
  }

  if (type === 'YTH') {
    valueA = Math.max(
      ...a.history
        .slice(
          0,
          a.history.findIndex(
            ({ date }) => moment(date).format('MM-DD') === '01-01'
          )
        )
        .map(({ [currency]: val }) => val)
    );
    valueB = Math.max(
      ...b.history
        .slice(
          0,
          b.history.findIndex(
            ({ date }) => moment(date).format('MM-DD') === '01-01'
          )
        )
        .map(({ [currency]: val }) => val)
    );
  }

  if (type === 'CATEGORY') {
    valueA = objA.general?.category ?? '';
    valueB = objB.general?.category ?? '';
  }

  if (type === 'COMPANY') {
    valueA = objA.etfData?.companyName ?? '';
    valueB = objB.etfData?.companyName ?? '';
  }

  if (type === 'NAV') {
    valueA = objA.mutualFundData?.nav ? +objA.mutualFundData.nav : 0;
    valueB = objB.mutualFundData?.nav ? +objB.mutualFundData.nav : 0;
  }

  if (type === 'YIELD') {
    valueA =
      objA.mutualFundData?.yield || objA.etfData?.yield
        ? Number(objA.mutualFundData?.yield ?? objA.etfData?.yield)
        : 0;
    valueB =
      objB.mutualFundData?.yield || objB.etfData?.yield
        ? Number(objB.mutualFundData?.yield ?? objB.etfData?.yield)
        : 0;
  }

  if (type === 'YIELD 1 YEAR') {
    valueA = objA.mutualFundData?.yield1YearYtd
      ? +objA.mutualFundData.yield1YearYtd
      : 0;
    valueB = objB.mutualFundData?.yield1YearYtd
      ? +objB.mutualFundData.yield1YearYtd
      : 0;
  }

  if (type === 'YIELD 3 YEAR') {
    valueA = objA.mutualFundData?.yield3YearYtd
      ? +objA.mutualFundData.yield3YearYtd
      : 0;
    valueB = objB.mutualFundData?.yield3YearYtd
      ? +objB.mutualFundData.yield3YearYtd
      : 0;
  }

  if (type === 'YIELD 5 YEAR') {
    valueA = objA.mutualFundData?.yield5YearYtd
      ? +objA.mutualFundData.yield5YearYtd
      : 0;
    valueB = objB.mutualFundData?.yield5YearYtd
      ? +objB.mutualFundData.yield5YearYtd
      : 0;
  }

  return getSortValue(valueA, valueB);
};
